(() => {
  const states = new Map();
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const nativeCommands = 'commandForElement' in HTMLButtonElement.prototype;

  function sync(state) {
    state.button.setAttribute('aria-label', state.video.paused ? state.button.dataset.playLabel : state.button.dataset.pauseLabel);
  }

  function shouldPlay(state) {
    if (document.hidden || state.intent === 'pause') return false;
    if (state.intent === 'play') return state.visible;
    return state.autoplay && !motion.matches && state.autoVisible;
  }

  function apply(state) {
    if (!shouldPlay(state)) {
      state.video.pause();
      sync(state);
      return;
    }
    try {
      const playing = state.video.play();
      if (playing && typeof playing.then === 'function') {
        playing.then(() => {
          if (!shouldPlay(state)) state.video.pause();
          sync(state);
        }).catch(() => sync(state));
      }
    } catch {
      sync(state);
    }
  }

  function toggle(state) {
    state.intent = state.video.paused ? 'play' : 'pause';
    apply(state);
  }

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      const state = states.get(entry.target);
      if (!state) return;
      state.visible = entry.isIntersecting && entry.intersectionRatio > 0;
      state.autoVisible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
      apply(state);
    });
  }, { threshold: [0, 0.5] }) : null;

  function init(root) {
    root.querySelectorAll('[data-tmx-home-video] button[data-tmx-video-toggle]').forEach(button => {
      const video = document.getElementById(button.getAttribute('aria-controls'));
      if (!(video instanceof HTMLVideoElement) || states.has(video)) return;
      const abort = new AbortController();
      const state = { video, button, abort, autoplay: video.autoplay, intent: 'auto', visible: true, autoVisible: true };
      states.set(video, state);
      video.addEventListener('command', event => {
        if (event.command !== '--tmx-toggle-playback' || event.source !== button) return;
        event.preventDefault();
        toggle(state);
      }, { signal: abort.signal });
      if (!nativeCommands) button.addEventListener('click', () => toggle(state), { signal: abort.signal });
      ['play', 'pause', 'ended', 'error'].forEach(name => video.addEventListener(name, () => sync(state), { signal: abort.signal }));
      video.addEventListener('loadedmetadata', () => apply(state), { signal: abort.signal });
      if (motion.matches) video.autoplay = false;
      button.hidden = false;
      sync(state);
      if (observer) {
        state.visible = false;
        state.autoVisible = false;
        observer.observe(video);
      }
      apply(state);
    });
  }

  document.addEventListener('visibilitychange', () => states.forEach(apply));
  motion.addEventListener('change', () => states.forEach(state => {
    if (motion.matches) {
      state.video.autoplay = false;
      if (state.intent === 'play') state.intent = 'auto';
    }
    apply(state);
  }));
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => states.forEach((state, video) => {
    if (!event.target.contains(state.button)) return;
    state.abort.abort();
    observer?.unobserve(video);
    video.pause();
    states.delete(video);
  }));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
})();
